import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, PlayCircle, User as UserIcon, Search, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useChannel } from '../contexts/ChannelContext';
import { useUserAuth } from '../contexts/UserAuthContext';
import useKeyboardInset from '../hooks/useKeyboardInset';
import { readInboxRole, INBOX_ROLE_EVENT } from './InboxRoleSwitcher';
import { createApiClient } from '../lib/apiClient';
import { usePausableInterval } from '../lib/usePausableInterval';
import { resolveImageUrl } from './ImageUpload';
import { FlytWings } from './FlytWings';
import { isNativeApp } from '../lib/nativeBridge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inboxApi = createApiClient();

/**
 * BottomNav — Instagram-style mobile bottom navigation, channel-aware.
 *   • Home (channel home: /, /visionary, /flyt)
 *   • Network (channel network: /network, /visionary/network — disabled on FLYT)
 *   • Search (/search)
 *   • Profile (/profile) — shows avatar when signed in
 *
 * Mobile-only (≤768px). Hidden on /admin and /login.
 *
 * Badge behavior (per user request):
 *   Red badge shows the number of NEW saved items since the user last visited
 *   /profile. It clears as soon as they tap the profile tab. New saves bump it
 *   back up. Stored per-user in localStorage so the count persists across
 *   reloads but never leaks across accounts.
 */
const seenKey = (userId) => `dl_saved_seen_count_${userId}`;

const BottomNav = () => {
  const channel = useChannel();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUserAuth();
  // Track the iOS soft keyboard so the dock can slide off-screen while
  // the user is typing in the chat composer.
  const keyboardInset = useKeyboardInset();

  const path = location.pathname;
  const isProfileActive = path.startsWith('/profile');

  const totalSaved = user
    ? (user.saved_stories?.length || 0) + (user.saved_athletes?.length || 0) + (user.saved_videos?.length || 0)
    : 0;

  // Track how many saves the user has already "seen" on their profile.
  // Default: treat ALL existing saves on first mount as already-seen so the
  // badge only fires on NEW activity after first sign-in.
  const [seenCount, setSeenCount] = useState(0);

  // Initialize or migrate seenCount whenever the signed-in user changes.
  useEffect(() => {
    if (!user) { setSeenCount(0); return; }
    let stored = null;
    try {
      const raw = localStorage.getItem(seenKey(user.user_id));
      stored = raw === null ? null : Number(raw);
    } catch { /* ignore */ }
    if (stored === null || Number.isNaN(stored)) {
      // First time we've seen this user — treat current total as the baseline.
      try { localStorage.setItem(seenKey(user.user_id), String(totalSaved)); } catch { /* ignore */ }
      setSeenCount(totalSaved);
    } else {
      setSeenCount(stored);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  // Clear the badge the moment they land on /profile.
  useEffect(() => {
    if (!user) return;
    if (isProfileActive && seenCount !== totalSaved) {
      try { localStorage.setItem(seenKey(user.user_id), String(totalSaved)); } catch { /* ignore */ }
      setSeenCount(totalSaved);
    }
  }, [isProfileActive, totalSaved, seenCount, user]);

  // If the user unsaves things while NOT on /profile, ratchet the baseline
  // down so the badge never goes negative or pops weirdly later.
  useEffect(() => {
    if (!user) return;
    if (!isProfileActive && totalSaved < seenCount) {
      try { localStorage.setItem(seenKey(user.user_id), String(totalSaved)); } catch { /* ignore */ }
      setSeenCount(totalSaved);
    }
  }, [totalSaved, seenCount, isProfileActive, user]);

  const unreadCount = Math.max(0, totalSaved - seenCount);

  // ── ON-DEMAND POLL NONCE ──
  // Bumping this triggers every message-badge polling effect (founder,
  // roster, athlete, client) to re-fetch immediately, on top of their
  // 30-60s intervals. We bump it whenever the user lands on a message
  // route (so the badge reconciles with the server's just-updated read
  // state) or when any message page fires a `messages:read` event
  // (e.g. after opening a specific thread).
  const [pollNonce, setPollNonce] = useState(0);
  const bumpPoll = () => setPollNonce(n => n + 1);
  useEffect(() => {
    const onRead = () => bumpPoll();
    window.addEventListener('messages:read', onRead);
    return () => window.removeEventListener('messages:read', onRead);
  }, []);

  // ── FOUNDER INBOX BADGE ──
  // Only fetches when the signed-in user is in the hard-coded founder
  // allowlist (server flag is_founder=true). Polls every 30s.
  const [founderUnread, setFounderUnread] = useState(0);
  const isFounder = !!user?.is_founder;
  const isInboxActive = path.startsWith('/inbox');
  const tickFounder = useCallback(async () => {
    try {
      const res = await inboxApi.get('/founder/inbox', { params: { limit: 50 } });
      setFounderUnread(res.data?.unread || 0);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (!isFounder) { setFounderUnread(0); return; }
    tickFounder();
  }, [isFounder, pollNonce, tickFounder]);
  // iter-perf · Pauses while the app is backgrounded instead of polling
  // an unread-count badge nobody can see, catching up the instant the
  // app is foregrounded again.
  usePausableInterval(tickFounder, 30000, { enabled: isFounder });

  // Any client_portal user (primary contact OR added team member, with
  // or without a brand_slug) gets the middle "messages" tab in the
  // bottom nav. Tapping it opens /messages — a full-screen thread with
  // the FLYT team. This replaces the in-line "Chat" block that used to
  // sit at the bottom of /portal, so the chat lives in one place.
  const isClientWithBrand = !!user?.client_portal;
  const isMessagesActive = path.startsWith('/messages');
  // Tiny unread counter — number of admin notes posted AFTER the user
  // last opened /messages. We poll the standard /client-portal/me
  // every 60s while the tab is mounted.
  const [clientUnread, setClientUnread] = useState(0);
  useEffect(() => {
    if (!isClientWithBrand) { setClientUnread(0); }
  }, [isClientWithBrand]);
  const tickClient = useCallback(async () => {
    if (!isClientWithBrand) return;
    try {
      const key = `dl_msg_seen_${user.user_id}`;
      const r = await axios.get(`${BACKEND_URL}/api/client-portal/me`, { withCredentials: true });
      const notes = (r.data?.notes || []).filter((n) => n.author_role === 'founder');
      const last = notes.length ? notes[notes.length - 1].created_at : null;
      if (!last) { setClientUnread(0); return; }
      const seen = (() => { try { return localStorage.getItem(key) || ''; } catch { return ''; } })();
      const unread = notes.filter((n) => (n.created_at || '') > seen).length;
      setClientUnread(unread);
    } catch { /* ignore */ }
  }, [isClientWithBrand, user?.user_id]);
  useEffect(() => {
    if (isClientWithBrand) tickClient();
  }, [isClientWithBrand, user?.user_id, pollNonce, tickClient]);
  usePausableInterval(tickClient, 60000, { enabled: isClientWithBrand });
  // Clear the badge the moment the client lands on /messages.
  useEffect(() => {
    if (!isClientWithBrand) return;
    if (isMessagesActive && clientUnread > 0) {
      try { localStorage.setItem(`dl_msg_seen_${user.user_id}`, new Date().toISOString()); } catch { /* ignore */ }
      setClientUnread(0);
    }
  }, [isMessagesActive, isClientWithBrand, clientUnread, user?.user_id]);

  // ── AGENT / BRAND / ATHLETE PORTAL DETECTION ──
  // A single /me/portals fetch feeds three possible middle-nav routes:
  //   • roster   → /roster/messages   (agent OR brand)
  //   • athlete  → /athlete/messages  (athlete dashboard side)
  //   • client   → /messages          (client_portal / brand chat)
  // Users who own more than one surface can switch via <InboxRoleSwitcher/>
  // on /profile — preference persisted to localStorage and broadcast via
  // the `inbox-role:changed` event.
  const [rosterOwner, setRosterOwner] = useState(null);      // { kind, accent }
  const [athletePortal, setAthletePortal] = useState(null);  // { accent }
  const [rosterUnread, setRosterUnread] = useState(0);
  const [athleteUnread, setAthleteUnread] = useState(0);
  const isRosterMessagesActive = path.startsWith('/roster/messages');
  const isAthleteMessagesActive = path.startsWith('/athlete/messages');
  useEffect(() => {
    if (!user) { setRosterOwner(null); setAthletePortal(null); return undefined; }
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/me/portals`, { withCredentials: true })
      .then(r => {
        if (cancelled) return;
        const list = r.data.portals || [];
        setRosterOwner(list.find(p => p.kind === 'agent' || p.kind === 'brand') || null);
        setAthletePortal(list.find(p => p.kind === 'athlete') || null);
      })
      .catch(() => {
        if (cancelled) return;
        setRosterOwner(null); setAthletePortal(null);
      });
    return () => { cancelled = true; };
  }, [user?.user_id]);
  const tickRoster = useCallback(async () => {
    if (!rosterOwner) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/me/roster/messages`, { withCredentials: true });
      const total = (r.data?.threads || []).reduce((s, t) => s + (t.unread || 0), 0);
      setRosterUnread(total);
    } catch { /* ignore */ }
  }, [rosterOwner]);
  useEffect(() => {
    if (!rosterOwner) { setRosterUnread(0); return; }
    tickRoster();
  }, [rosterOwner, pollNonce, tickRoster]);
  usePausableInterval(tickRoster, 60000, { enabled: !!rosterOwner });

  const tickAthlete = useCallback(async () => {
    if (!athletePortal) return;
    try {
      const r = await axios.get(`${BACKEND_URL}/api/me/athlete/roster-threads`, { withCredentials: true });
      const total = (r.data?.threads || []).reduce((s, t) => s + (t.unread || 0), 0);
      setAthleteUnread(total);
    } catch { /* ignore */ }
  }, [athletePortal]);
  useEffect(() => {
    if (!athletePortal) { setAthleteUnread(0); return; }
    tickAthlete();
  }, [athletePortal, pollNonce, tickAthlete]);
  usePausableInterval(tickAthlete, 60000, { enabled: !!athletePortal });

  // ── ROUTE-CHANGE BADGE CLEAR ──
  // The instant the user lands on a message route, zero the local badge
  // (optimistic: the thread's GET marks-read on the server side, so the
  // next poll should also return 0). Also bump `pollNonce` so both the
  // roster and athlete pollers re-fetch and reconcile with the server.
  // Client-portal /messages already had this — extended to founder /inbox,
  // roster and athlete routes here so every message surface behaves the
  // same way.
  useEffect(() => {
    if (isRosterMessagesActive && rosterUnread > 0) {
      setRosterUnread(0);
      // Reconcile shortly after the thread's GET has marked read server-side.
      setTimeout(bumpPoll, 600);
    }
  }, [isRosterMessagesActive, rosterUnread]);
  useEffect(() => {
    if (isAthleteMessagesActive && athleteUnread > 0) {
      setAthleteUnread(0);
      setTimeout(bumpPoll, 600);
    }
  }, [isAthleteMessagesActive, athleteUnread]);

  // ── HOME-SCREEN APP BADGE (iOS PWA + Chrome/Edge desktop PWA) ──
  // Sums unread across every message surface the user owns (founder
  // inbox, agent/brand roster, athlete dashboard, client portal) and
  // mirrors the total to `navigator.setAppBadge` — so users who added
  // flythouse.com to their iOS Home Screen see the red "3" bubble on
  // the FLYT app icon exactly like a native app. iOS 16.4+ supports
  // the Badging API for PWAs; older iOS silently no-ops.
  //
  // This is a single source of truth: no other effect calls setAppBadge
  // for message unread. Founder-inbox actions (mark-read / mark-all)
  // still call setAppBadge directly for instantaneous feedback, but
  // this consolidator reconciles on every count change.
  useEffect(() => {
    const total = (founderUnread || 0) + (rosterUnread || 0) + (athleteUnread || 0) + (clientUnread || 0);
    import('../lib/appBadge').then((m) => { m.setAppBadge(total); }).catch(() => {});
  }, [founderUnread, rosterUnread, athleteUnread, clientUnread]);

  // ── MULTI-INBOX PREFERENCE ──
  // When the user owns 2+ inbox surfaces, they can pick which one opens
  // from the middle bottom-nav icon via the InboxRoleSwitcher on /profile.
  const [inboxRolePref, setInboxRolePref] = useState(() => readInboxRole(user?.user_id));
  useEffect(() => {
    setInboxRolePref(readInboxRole(user?.user_id));
  }, [user?.user_id]);
  useEffect(() => {
    const onChange = (e) => setInboxRolePref(e?.detail?.choice || readInboxRole(user?.user_id));
    window.addEventListener(INBOX_ROLE_EVENT, onChange);
    return () => window.removeEventListener(INBOX_ROLE_EVENT, onChange);
  }, [user?.user_id]);
  // Available surfaces + default priority (roster > client > athlete)
  // so single-role users still land on the surface that used to win
  // before we added the switcher.
  const availableInboxes = [];
  if (rosterOwner) availableInboxes.push('roster');
  if (isClientWithBrand) availableInboxes.push('client');
  if (athletePortal) availableInboxes.push('athlete');
  const hasMultipleInboxes = availableInboxes.length > 1;
  // Only respect the pref when the picked surface is actually available.
  const effectiveInbox = (
    hasMultipleInboxes && inboxRolePref && availableInboxes.includes(inboxRolePref)
      ? inboxRolePref
      : (availableInboxes[0] || null)
  );
  const showRosterMessages = effectiveInbox === 'roster';
  const showClientMessages = effectiveInbox === 'client';
  const showAthleteMessages = effectiveInbox === 'athlete';

  // Refs for the draggable lozenge — declared above the early return so
  // they're always created in the same order (React hooks rules).
  const stripRef = useRef(null);
  const lozengeRef = useRef(null);
  const dragState = useRef({ startX: 0, startIdx: 0, dragging: false, lastDelta: 0 });
  // Hide the dock entirely on the dedicated `/messages` route — that
  // page renders as a full-screen chat shell with its own back button,
  // so the dock would just compete for the safe-area space the
  // composer needs to sit flush above the iOS home indicator.
  // Also hide while any in-app chat thread (e.g. Network Clients on
  // /inbox) is open — the chat panel dispatches `chat:state` events
  // so we don't need to know about every chat surface here.
  const [chatOpen, setChatOpen] = useState(
    typeof document !== 'undefined' && document.body?.getAttribute('data-chat-open') === '1',
  );
  useEffect(() => {
    const onChatState = (e) => setChatOpen(!!e?.detail?.open);
    window.addEventListener('chat:state', onChatState);
    return () => window.removeEventListener('chat:state', onChatState);
  }, []);
  // Signed-out users on the native app's home tab see <WelcomeGate/>
  // (Sign Up / Sign In only, no browsing) — the dock has nothing useful
  // to navigate to underneath it, so it stays hidden there too. The
  // gate itself is portalled above everything regardless (see
  // WelcomeGate.jsx), this is just belt-and-suspenders.
  const isWelcomeGateRoute = !user && path === '/' && isNativeApp();
  const adminBlocked = path.startsWith('/admin') || path.startsWith('/Penelopesims') || path === '/login' || path.startsWith('/payment-') || path === '/messages' || path.startsWith('/roster/messages') || path.startsWith('/athlete/messages') || chatOpen || isWelcomeGateRoute;

  const isFlyt = channel.id === 'flyt';
  const isVisionary = channel.id === 'dreams_visionary';

  // Per user request: the Home tab always lands on the League Network
  // home (`/`), regardless of which channel the user is currently in.
  // That's the canonical "home page of the network" — anyone tapping
  // the house icon from a story, a FLYT case study, or Visionary always
  // gets returned to the same anchor page.
  const homePath = '/';
  // iter55 · The Stories bottom-nav tab now routes to the dedicated
  // Stories destination (`/stories`) — the new cinematic library — so
  // it stops duplicating the League Network home. Channel-aware so
  // Visionary and FLYT get their own Stories pages.
  const storiesPath = channel.id === 'dreams_visionary'
    ? '/visionary/stories'
    : channel.id === 'flyt'
      ? '/flyt/stories'
      : '/stories';
  // The Network tab still lives at /network for the middle athlete
  // pill on Home mode. Keep it as the fallback network path variable.
  const networkPath = storiesPath;

  // Network-the-feature (athletes & league pages) lives at `/` and
  // `/network/<slug>` — both should light up the **Home** icon. The
  // **Stories** (Play) tab is the editorial feed at exact `/network`
  // only — story listings, breaking, mission. Once the user clicks into
  // an athlete profile we hop them into Home so the nav doesn't lie about
  // which surface they're on.
  // Stories tab active on /stories (any channel) or on a story detail page
  const isStoriesRoute = /^\/(?:flyt\/|visionary\/)?stories(\/|$)/.test(path);
  const isAthleteNetworkRoute = path.startsWith('/network/');
  const isHomeActive = path === homePath || isAthleteNetworkRoute || path === '/network';
  const isNetworkActive = isStoriesRoute;

  // ── Story detail "tab carry-over" ──────────────────────────────
  // When the user opens a story (`/stories/<slug>` or any channel
  // equivalent), the bottom nav should keep highlighting the tab the
  // story actually lives on — Network or Stories — instead of going
  // dark. We look up the story's `section` field once and cache it so
  // navigation between stories stays snappy.
  const storyMatch = /^\/(?:flyt\/|visionary\/)?stories\/([^/?#]+)/.exec(path);
  const currentStorySlug = storyMatch?.[1] || null;
  const [storySectionCache, setStorySectionCache] = useState({});
  const currentStorySection = currentStorySlug ? storySectionCache[currentStorySlug] : null;
  useEffect(() => {
    if (!currentStorySlug) return;
    if (storySectionCache[currentStorySlug] !== undefined) return;
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/stories/${currentStorySlug}`)
      .then(r => {
        if (cancelled) return;
        setStorySectionCache(prev => ({ ...prev, [currentStorySlug]: r.data?.section || 'stories' }));
      })
      .catch(() => {
        if (cancelled) return;
        setStorySectionCache(prev => ({ ...prev, [currentStorySlug]: 'stories' }));
      });
    return () => { cancelled = true; };
  }, [currentStorySlug, storySectionCache]);

  // Apply the carry-over: if we're on a story page and we know its
  // section, force the matching tab to be the active one.
  const onStoryPage = !!currentStorySlug;
  const storyOnNetwork = onStoryPage && currentStorySection === 'network';
  const storyOnStories = onStoryPage && currentStorySection && currentStorySection !== 'network';
  // Recompute active flags with the story override layered on top of the
  // raw URL match.
  const isHomeActiveFinal = isHomeActive || storyOnNetwork;
  const isNetworkActiveFinal = isNetworkActive || storyOnStories;

  // Pages within the (light) Dreams League channel that are actually dark-
  // themed full-bleed experiences. We force a dark BottomNav so the nav
  // doesn't fight the page background.
  // After the home swap `/` (HomeIndex with NetworkPage stacked on top of
  // the dark-cascaded Homepage) is treated as dark too — that way the
  // bottom nav reads as the same frosted grey material as the top navbar
  // and Founder LiveStats widget instead of flashing a bright light pill
  // over the dark scroll.
  // BottomNav now follows the same "dark glass everywhere" rule as the
  // top Navbar — the user wanted a unified frosted-grey chrome across the
  // entire site so every page feels like the Network tab. FLYT keeps its
  // own native dark theme; Visionary is always dark; everything else
  // (League channel) is now dark too regardless of route.
  const isDarkRoute = true;
  const wantsDark = isFlyt || isVisionary || isDarkRoute;
  const isSearchActive = path === '/search';
  // Home/Network active flags already defined above. isProfileActive too.

  // ── LIQUID GLASS THEME ────────────────────────────────────────────
  // Apple-style floating glass capsule: heavy backdrop blur, frosted grey
  // fill (matches the Navbar + Founder LiveStats widget — same
  // rgba(20,20,22,0.55) recipe), soft inner highlight, drop shadow for
  // depth, and an active "lozenge" that morphs in behind the current tab.
  // Color of pills/accent stays channel-aware so you always know where
  // you are.
  const wrapperTheme = wantsDark
    ? 'liquid-glass-bottom-nav ring-1 ring-white/15 text-white shadow-[0_18px_50px_-12px_rgba(0,0,0,0.55)]'
    : 'bg-white/55 ring-1 ring-black/10 text-black shadow-[0_18px_50px_-15px_rgba(0,0,0,0.25)]';
  const accentClass = isFlyt
    ? 'text-yellow-300'
    : (isVisionary || isDarkRoute)
      ? 'text-white'
      : 'text-blue-600';
  const inactiveClass = wantsDark ? 'text-gray-400' : 'text-gray-500';
  const lozengeBg = wantsDark
    ? 'bg-white/[0.18] ring-1 ring-white/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]'
    : 'bg-black/[0.08] ring-1 ring-black/10';

  // === DRAGGABLE LIQUID-LOZENGE TRACKING ===
  // The lozenge floats inside the tab strip and tracks the user's finger
  // when they drag across it. We use a minimal data array (no handler
  // refs) so this can sit above where handleHomeClick is defined.
  // Snap-navigate at touchEnd just routes to `to`; tap behaviors stay on
  // each <Link>.
  // iter654 · Athlete persona gets a dedicated 5-tab layout with a
  // center FLYT Wings tab that opens the personal Media Hub. Only the
  // athlete persona sees it in V1 — brand/agent/creator/program layouts
  // are unchanged.
  const isAthletePersona = !!user && (
    user.persona === 'athlete' || user.is_verified_athlete === true ||
    (user.linked_athlete && (typeof user.linked_athlete === 'string' || (user.linked_athlete.slug && !user.is_founder)))
  );
  const isMediaHubActive = path.startsWith('/me/media');

  // iter695 · "New photos" badge on the Wings tab. Counts folders with
  // freshly-uploaded, still-unopened photos. Re-fetches on every route
  // change so the dot clears the instant the athlete taps into a folder
  // (opening a folder marks it seen server-side). Only runs for athletes.
  const [newMediaCount, setNewMediaCount] = useState(0);
  useEffect(() => {
    if (!isAthletePersona) { setNewMediaCount(0); return; }
    let alive = true;
    const fetchCount = async () => {
      try {
        // Combine BOTH signals that power the "new photos" state so the
        // wings dot always matches the dot on the athlete profile:
        //  1. /me/media/new-count — folders with fresh slug-matched assets
        //  2. /me/athlete/game-media unread beacons — the profile's
        //     "N new games waiting for review" (fires even before AI
        //     matches contributor photos to the athlete).
        const [nc, gm] = await Promise.allSettled([
          axios.get(`${BACKEND_URL}/api/me/media/new-count`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/me/athlete/game-media`, { withCredentials: true }),
        ]);
        const countA = nc.status === 'fulfilled' ? (Number(nc.value?.data?.count) || 0) : 0;
        const events = gm.status === 'fulfilled' ? (gm.value?.data?.recent_events || []) : [];
        const countB = events.filter((e) => !e.read_at).length;
        if (alive) setNewMediaCount(countA + countB);
      } catch { /* ignore — badge just stays hidden */ }
    };
    fetchCount();
    // iter689e · The instant they tap into the Media hub, clear the dot
    // locally AND tell the server to mark every unread beacon seen — so
    // it doesn't persist or pop back on the next poll.
    if (isMediaHubActive) {
      setNewMediaCount(0);
      axios.post(`${BACKEND_URL}/api/me/media/mark-all-seen`, {}, { withCredentials: true }).catch(() => {});
    }
    // Opening a folder marks it "seen" on the server as that page loads;
    // re-poll shortly after so the dot clears right after tapping in.
    const t = path.includes('/me/media/games/') ? setTimeout(fetchCount, 1200) : null;
    return () => { alive = false; if (t) clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAthletePersona, path]);


  const tabs = isFounder ? [
    { to: homePath, active: isHomeActiveFinal },
    { to: networkPath, active: isNetworkActiveFinal, disabled: !networkPath },
    { to: '/inbox', active: isInboxActive },
    { to: '/search', active: isSearchActive },
    { to: '/profile', active: isProfileActive },
  ] : isAthletePersona ? [
    { to: homePath, active: isHomeActiveFinal },
    { to: networkPath, active: isNetworkActiveFinal, disabled: !networkPath },
    { to: '/me/media', active: isMediaHubActive },
    { to: '/search', active: isSearchActive },
    { to: '/profile', active: isProfileActive },
  ] : showRosterMessages ? [
    // Agent/brand roster messages take precedence over client_portal so a
    // user with BOTH roles (e.g. Richard Swart) sees their roster inbox
    // in the middle nav — matches the JSX render precedence below.
    // Users with 2+ inbox surfaces can flip this via <InboxRoleSwitcher/>
    // on /profile.
    { to: homePath, active: isHomeActiveFinal },
    { to: networkPath, active: isNetworkActiveFinal, disabled: !networkPath },
    { to: '/roster/messages', active: isRosterMessagesActive },
    { to: '/search', active: isSearchActive },
    { to: '/profile', active: isProfileActive },
  ] : showAthleteMessages ? [
    { to: homePath, active: isHomeActiveFinal },
    { to: networkPath, active: isNetworkActiveFinal, disabled: !networkPath },
    { to: '/athlete/messages', active: isAthleteMessagesActive },
    { to: '/search', active: isSearchActive },
    { to: '/profile', active: isProfileActive },
  ] : showClientMessages ? [
    { to: homePath, active: isHomeActiveFinal },
    { to: networkPath, active: isNetworkActiveFinal, disabled: !networkPath },
    { to: '/messages', active: isMessagesActive },
    { to: '/search', active: isSearchActive },
    { to: '/profile', active: isProfileActive },
  ] : [
    { to: homePath, active: isHomeActiveFinal },
    { to: networkPath, active: isNetworkActiveFinal, disabled: !networkPath },
    { to: '/search', active: isSearchActive },
    { to: '/profile', active: isProfileActive },
  ];
  const activeIndex = tabs.findIndex(t => t.active);
  const safeActiveIndex = activeIndex < 0 ? 0 : activeIndex;

  // px-1.5 on the strip = 6px on each side. Keep the lozenge inside the
  // strip's content area so it never spills over the rounded outer edges
  // of the floating capsule (looked clipped/oversized on iPad + landscape).
  const STRIP_PAD_X = 6;

  const applyLozengeTransform = (idx, deltaPx = 0) => {
    if (!lozengeRef.current || !stripRef.current) return;
    const contentWidth = stripRef.current.clientWidth - STRIP_PAD_X * 2;
    const tabWidth = contentWidth / tabs.length;
    const baseX = idx * tabWidth;
    lozengeRef.current.style.transform = `translateX(${baseX + deltaPx}px)`;
  };

  // Keep the lozenge in sync when the active tab changes via tap or URL change.
  useEffect(() => {
    if (adminBlocked) return;
    applyLozengeTransform(safeActiveIndex, 0);
    if (lozengeRef.current) {
      lozengeRef.current.style.transition = 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1)';
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeActiveIndex, tabs.length, adminBlocked]);

  const onStripTouchStart = (e) => {
    if (e.touches.length !== 1 || !stripRef.current) return;
    dragState.current.startX = e.touches[0].clientX;
    dragState.current.startIdx = safeActiveIndex;
    dragState.current.dragging = true;
    dragState.current.lastDelta = 0;
    dragState.current.lastTickIdx = safeActiveIndex;
    // Disable the snap transition while finger is down — we update transform
    // directly on every frame so the lozenge feels glued to the finger.
    if (lozengeRef.current) lozengeRef.current.style.transition = 'none';
  };

  // iter484 · Tiny haptic tick — fires every time the lozenge slides
  // past a tab boundary during the drag. Uses the Capacitor
  // @capacitor/haptics `selection` API on native iOS (a proper wheel-
  // picker tick that feels made for scrubbing), and keeps the
  // `navigator.vibrate` fallback for Android/Chrome. Also pulses the
  // lozenge as a visual stand-in.
  const fireTick = () => {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(5); } catch { /* ignore */ }
    }
    try {
      import('../lib/nativeBridge').then((m) => m.haptic('selection')).catch(() => {});
    } catch { /* noop */ }
    if (lozengeRef.current) {
      // Visual pulse — only the box scales, not its translate
      const el = lozengeRef.current;
      el.style.transitionProperty = 'none';
      el.style.filter = 'brightness(1.4)';
      // Schedule a return-to-normal on the next frame
      requestAnimationFrame(() => {
        if (!el) return;
        el.style.transitionProperty = 'filter';
        el.style.transitionDuration = '180ms';
        el.style.filter = 'brightness(1)';
      });
    }
  };

  const onStripTouchMove = (e) => {
    if (!dragState.current.dragging || !stripRef.current) return;
    const dx = e.touches[0].clientX - dragState.current.startX;
    const contentWidth = stripRef.current.clientWidth - STRIP_PAD_X * 2;
    const tabWidth = contentWidth / tabs.length;
    const maxX = (tabs.length - 1) * tabWidth;
    const baseX = dragState.current.startIdx * tabWidth;
    // Clamp + add a rubber-band feel past the edges
    let nextX = baseX + dx;
    if (nextX < 0) nextX = nextX * 0.35;
    if (nextX > maxX) nextX = maxX + (nextX - maxX) * 0.35;
    dragState.current.lastDelta = nextX - baseX;
    if (lozengeRef.current) {
      lozengeRef.current.style.transform = `translateX(${nextX}px)`;
    }
    // Fire a haptic + visual tick whenever the lozenge crosses into the
    // hover-zone of a new tab. We snap-test by rounding to nearest tab idx.
    const hoverIdx = Math.max(0, Math.min(tabs.length - 1, Math.round(nextX / tabWidth)));
    if (hoverIdx !== dragState.current.lastTickIdx) {
      dragState.current.lastTickIdx = hoverIdx;
      fireTick();
    }
  };

  const onStripTouchEnd = () => {
    if (!dragState.current.dragging || !stripRef.current) return;
    dragState.current.dragging = false;
    // Snap to the nearest tab based on current visual position
    const contentWidth = stripRef.current.clientWidth - STRIP_PAD_X * 2;
    const tabWidth = contentWidth / tabs.length;
    const baseX = dragState.current.startIdx * tabWidth;
    const finalX = baseX + dragState.current.lastDelta;
    const snappedIdx = Math.max(0, Math.min(tabs.length - 1, Math.round(finalX / tabWidth)));
    // Re-enable transition for the snap, then apply final position
    if (lozengeRef.current) {
      lozengeRef.current.style.transition = 'transform 380ms cubic-bezier(0.34, 1.2, 0.45, 1)';
    }
    applyLozengeTransform(snappedIdx, 0);
    // Navigate only if we actually moved to a new tab, and that tab is reachable
    if (snappedIdx !== safeActiveIndex) {
      const tgt = tabs[snappedIdx];
      if (tgt?.to && !tgt.disabled) {
        // iter484 · Stronger landing thump — Medium impact on native iOS
        // (the "click" of locking onto the destination tab) with the
        // vibrate pattern fallback for Android.
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          try { navigator.vibrate([4, 8, 14]); } catch { /* ignore */ }
        }
        try {
          import('../lib/nativeBridge').then((m) => m.haptic('medium')).catch(() => {});
        } catch { /* noop */ }
        navigate(tgt.to);
      }
      // Disabled tabs (Network on FLYT) simply snap back — no nav.
    }
  };

  // iter484 · Light haptic on every tab tap AND on the horizontal-
  // drag slide so switching between Home/Play/Search/Profile feels
  // tactile on native iOS. The pulse fires only when the tab actually
  // changes so re-tapping the current tab doesn't buzz.
  const fireNavHaptic = (nextPath) => {
    if (nextPath && nextPath === location.pathname) return;
    try {
      // Lazy import so the web build doesn't ship the module on first paint.
      import('../lib/nativeBridge').then((m) => m.haptic('light')).catch(() => {});
    } catch { /* noop */ }
  };

  const Item = ({ to, icon, label, active, disabled, testid, onClick }) => {
    const colorClass = active ? accentClass : inactiveClass;
    const showAsDisabled = disabled && !onClick;
    // No per-item lozenge anymore — the strip-level floating lozenge
    // (positioned absolutely behind all items) tracks the active tab and
    // also follows the user's finger when they drag horizontally.
    // Icon-only layout — no labels, bigger tap targets, tall pill background.
    const body = (
      <span className={`relative flex items-center justify-center flex-1 py-2 active:scale-90 transition ${
        showAsDisabled ? 'opacity-40 pointer-events-none' : ''
      }`}>
        <span className={`relative ${colorClass} transition-transform ${active ? 'scale-105' : ''}`}>{icon}</span>
      </span>
    );
    if (disabled || !to) {
      return (
        <button
          type="button"
          onClick={(e) => { fireNavHaptic(); onClick?.(e); }}
          className="flex-1 flex"
          aria-disabled={onClick ? undefined : 'true'}
          aria-label={label}
          data-testid={testid}
        >{body}</button>
      );
    }
    return (
      <Link
        to={to}
        onClick={(e) => { fireNavHaptic(to); onClick?.(e); }}
        className="flex-1 flex"
        aria-label={label}
        data-testid={testid}
      >{body}</Link>
    );
  };

  // Tap Home while already on the home tab → smooth scroll to top.
  // React Router's Link is a no-op when path doesn't change, so we hook click.
  const handleHomeClick = (e) => {
    // Only intercept when the user is LITERALLY at the home path (`/`)
    // — scroll-to-top feels great there. When they're on an athlete
    // profile (/network/:slug), a story, or any other page, tapping
    // the home icon must actually navigate back to `/`. The default
    // <Link> behavior handles that when we skip preventDefault.
    if (path === homePath) {
      e.preventDefault();
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }
  };

  // FLYT has no Network channel yet — surface a soft "Coming Soon" toast
  // instead of letting the user think the tab is broken.
  const handleFlytNetworkTap = () => {
    toast('FLYT Network — coming soon', {
      description: "We're cooking the studio's behind-the-scenes feed.",
      duration: 2500,
    });
  };

  // Always-on channel-colored ring around the profile avatar in the bottom
  // nav. Makes it instantly obvious which channel ("tab") the user is in,
  // even before they look at the rest of the screen.
  // League → blue, Dreamers → white, FLYT → yellow.
  const channelRingClass = isFlyt
    ? 'ring-yellow-300'
    : isVisionary
      ? 'ring-white'
      : 'ring-blue-500';
  const channelRingOffsetClass = wantsDark ? 'ring-offset-black' : 'ring-offset-white';

  // iter656 · 20% smaller for all personas. Home/Search/Message icons
  // go from w-6 (24) to w-5 (20). Network/Play stays a touch larger
  // (w-6 vs w-7) so it doesn't disappear next to Home. Wings stay
  // deliberately BIGGER (w-10) because they're the athlete's identity
  // marker in the middle slot.
  const homeIcon = <Home className="w-5 h-5" strokeWidth={isHomeActiveFinal ? 2.4 : 1.9} />;
  const networkIcon = <PlayCircle className="w-6 h-6" strokeWidth={isNetworkActiveFinal ? 2.2 : 1.8} fill={isNetworkActiveFinal ? 'currentColor' : 'none'} fillOpacity={isNetworkActiveFinal ? 0.15 : 0} />;
  const searchIcon = <Search className="w-5 h-5" strokeWidth={isSearchActive ? 2.4 : 1.9} />;
  const [navAvatarFailed, setNavAvatarFailed] = useState(false);
  // Reset the failure flag whenever the picture URL changes so a new
  // upload gets a fresh shot at loading.
  useEffect(() => { setNavAvatarFailed(false); }, [user?.picture]);

  const profileIcon = (user?.picture && !navAvatarFailed) ? (
    <span className="relative inline-flex">
      <img loading="lazy" decoding="async"
        src={resolveImageUrl(user.picture, 80)}
        alt=""
        referrerPolicy="no-referrer"
        onError={() => setNavAvatarFailed(true)}
        className={`w-7 h-7 rounded-full object-cover ring-2 ring-offset-2 ${channelRingClass} ${channelRingOffsetClass} transition-[box-shadow] duration-300`}
        style={{
          // Soft glowing halo so the avatar reads like the iOS-26
          // floating-glass nav reference — channel ring up close, faint
          // bloom around it.
          boxShadow: isFlyt
            ? '0 0 0 1px rgba(0,0,0,0.45), 0 0 14px rgba(253, 224, 71, 0.55)'
            : isVisionary
              ? '0 0 0 1px rgba(0,0,0,0.45), 0 0 14px rgba(255, 255, 255, 0.45)'
              : '0 0 0 1px rgba(0,0,0,0.45), 0 0 14px rgba(59, 130, 246, 0.55)',
        }}
      />
      {unreadCount > 0 && <SavedBadge count={unreadCount} />}
    </span>
  ) : (
    <span className="relative inline-flex">
      <span className={`w-7 h-7 rounded-full ring-2 ring-offset-2 ${channelRingClass} ${channelRingOffsetClass} flex items-center justify-center ${wantsDark ? 'bg-white/10' : 'bg-gray-100'} transition-[box-shadow] duration-300`}>
        <UserIcon className="w-3.5 h-3.5" strokeWidth={isProfileActive ? 2.4 : 1.9} />
      </span>
      {unreadCount > 0 && <SavedBadge count={unreadCount} />}
    </span>
  );

  if (adminBlocked) return null;

  // Hide the dock the moment the iOS soft keyboard opens. iOS pushes
  // `position: fixed bottom-0` elements above the keyboard, so the dock
  // would otherwise sit sandwiched between the chat composer and the
  // keys — visually noisy and easy to mis-tap. We slide it out of view
  // with a fast transform so it disappears cleanly, then snaps back
  // when the keyboard dismisses.
  const keyboardOpen = keyboardInset > 24;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[70] 2xl:hidden flex justify-center pointer-events-none px-4 sm:px-6 pb-2"
      style={{
        // iter656 · 20% shorter bottom offset — matches the slimmer strip.
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 8px)',
        transform: keyboardOpen ? 'translate3d(0,120%,0)' : 'translate3d(0,0,0)',
        opacity: keyboardOpen ? 0 : 1,
        transition: 'transform 160ms ease-out, opacity 120ms ease-out',
        // Promote the dock to its own compositor layer so iOS Safari
        // doesn't repaint it on every URL-bar/scroll frame — fixes the
        // visible bottom-nav jitter Jay reported during page scroll.
        willChange: 'transform',
        backfaceVisibility: 'hidden',
      }}
      aria-hidden={keyboardOpen}
      data-testid="bottom-nav-host"
    >
      <nav
        className={`pointer-events-auto relative w-full max-w-md rounded-[32px] backdrop-blur-[28px] backdrop-saturate-150 transition-colors duration-300 overflow-hidden ${wrapperTheme}`}
        data-testid="bottom-nav"
        aria-label="Primary"
      >
        {/* Specular sheen along the top edge — sells the "liquid glass" look */}
        <span
          aria-hidden="true"
          className={`absolute inset-x-6 top-0 h-px ${wantsDark ? 'bg-white/30' : 'bg-white/80'}`}
        />
        <div
          ref={stripRef}
          className="relative flex items-stretch px-1 py-0.5 touch-pan-y"
          onTouchStart={onStripTouchStart}
          onTouchMove={onStripTouchMove}
          onTouchEnd={onStripTouchEnd}
          onTouchCancel={onStripTouchEnd}
          data-testid="bottom-nav-strip"
        >
          {/* Floating lozenge — single element that hugs the active tab
              and tracks the user's finger when they drag across the strip.
              Width is 1/N of the strip; transform is updated via direct
              DOM mutation during drag for 60fps smoothness. */}
          <span
            ref={lozengeRef}
            aria-hidden="true"
            className={`absolute top-1 bottom-1 rounded-[22px] pointer-events-none ${lozengeBg}`}
            style={{
              left: `${STRIP_PAD_X}px`,
              width: `calc((100% - ${STRIP_PAD_X * 2}px) / ${tabs.length})`,
              willChange: 'transform',
            }}
          />
          <Item to={homePath} icon={homeIcon} label="Home" active={isHomeActiveFinal} onClick={handleHomeClick} testid="bottom-nav-home" />
          <Item
            to={networkPath}
            icon={networkIcon}
            label="Stories"
            active={isNetworkActiveFinal}
            disabled={!networkPath}
            onClick={!networkPath ? handleFlytNetworkTap : undefined}
            testid="bottom-nav-network"
          />
          {isFounder && (
            <Item
              to="/inbox"
              icon={(
                <span className="relative inline-flex">
                  <MessageCircle className="w-5 h-5" strokeWidth={isInboxActive ? 2.4 : 1.9} />
                  {founderUnread > 0 && (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white/80 backdrop-blur shadow-md"
                      aria-label={`${founderUnread} new`}
                    >
                      {founderUnread > 99 ? '99+' : founderUnread}
                    </span>
                  )}
                </span>
              )}
              label="Inbox"
              active={isInboxActive}
              testid="bottom-nav-inbox"
            />
          )}
          {!isFounder && isAthletePersona && (
            <Item
              to="/me/media"
              icon={(
                // iter656 · Wings live in the middle slot for athletes
                // and are deliberately larger than every other icon so
                // they read as the brand identity marker, not just
                // another tab. Bumped from w-7 → w-10 (~57% bigger)
                // and forced pure white via <FlytWings/> filter.
                <span className={`relative inline-flex items-center justify-center transition-transform ${isMediaHubActive ? 'scale-110' : ''}`}>
                  <FlytWings className="w-10 h-10" />
                  {/* iter689e · Clean WHITE dot when new photos have
                      landed (unread game-media beacons). Clears the
                      instant the athlete taps into the Media hub — see
                      the mark-all-seen effect below — so it never
                      persists. */}
                  {newMediaCount > 0 && !isMediaHubActive && (
                    <span
                      className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-white ring-2 ring-black/80 shadow-[0_0_10px_rgba(255,255,255,0.9)] animate-in zoom-in duration-300"
                      data-testid="bottom-nav-media-new-badge"
                      aria-label="New photos in your media hub"
                    />
                  )}
                </span>
              )}
              label="Media"
              active={isMediaHubActive}
              testid="bottom-nav-media-hub"
            />
          )}
          {/* Non-founder inboxes — mutually exclusive with the Founder
              Inbox above. Founders don't get a duplicate message icon
              even when their linked_athlete auto-provisioning makes
              them look like a roster/athlete owner. */}
          {!isFounder && !isAthletePersona && showRosterMessages && (
            <Item
              to="/roster/messages"
              icon={(
                <span className="relative inline-flex">
                  <MessageCircle className="w-5 h-5" strokeWidth={isRosterMessagesActive ? 2.4 : 1.9} />
                  {rosterUnread > 0 && (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white/80 backdrop-blur shadow-md"
                      aria-label={`${rosterUnread} new`}
                    >
                      {rosterUnread > 99 ? '99+' : rosterUnread}
                    </span>
                  )}
                </span>
              )}
              label="Messages"
              active={isRosterMessagesActive}
              testid="bottom-nav-roster-messages"
            />
          )}
          {!isFounder && !isAthletePersona && showAthleteMessages && (
            <Item
              to="/athlete/messages"
              icon={(
                <span className="relative inline-flex">
                  <MessageCircle className="w-5 h-5" strokeWidth={isAthleteMessagesActive ? 2.4 : 1.9} />
                  {athleteUnread > 0 && (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white/80 backdrop-blur shadow-md"
                      aria-label={`${athleteUnread} new`}
                    >
                      {athleteUnread > 99 ? '99+' : athleteUnread}
                    </span>
                  )}
                </span>
              )}
              label="Messages"
              active={isAthleteMessagesActive}
              testid="bottom-nav-athlete-messages"
            />
          )}
          {!isFounder && !isAthletePersona && showClientMessages && (
            <Item
              to="/messages"
              icon={(
                <span className="relative inline-flex">
                  <MessageCircle className="w-5 h-5" strokeWidth={isMessagesActive ? 2.4 : 1.9} />
                  {clientUnread > 0 && (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white/80 backdrop-blur shadow-md"
                      aria-label={`${clientUnread} new`}
                    >
                      {clientUnread > 99 ? '99+' : clientUnread}
                    </span>
                  )}
                </span>
              )}
              label="Messages"
              active={isMessagesActive}
              testid="bottom-nav-messages"
            />
          )}
          <Item to="/search" icon={searchIcon} label="Search" active={isSearchActive} testid="bottom-nav-search" />
          <Item to="/profile" icon={profileIcon} label={user ? 'You' : 'Profile'} active={isProfileActive} testid="bottom-nav-profile" />
        </div>
      </nav>
    </div>
  );
};

export default BottomNav;

/**
 * Tiny red dopamine badge on the profile icon showing NEW saves since the
 * user last opened their profile. Caps at 99+. Positioned top-right of the icon.
 */
const SavedBadge = ({ count }) => (
  <span
    className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white/80 backdrop-blur shadow-md"
    aria-label={`${count} new`}
    data-testid="bottom-nav-saved-badge"
  >
    {count > 99 ? '99+' : count}
  </span>
);
